import { Injectable } from '@angular/core';
import { FormGroup, ValidationErrors } from '@angular/forms';
import { MachineDetailsForm } from '../../../core/models/machines/machine-form-models';
import { ContractorApiService } from '../../../core/services/api/contractor-api.service';
import { firstValueFrom } from 'rxjs';
import { SnackbarService } from '../../../core/services/error/snackbar.service';
import { ContractorDetailsDto } from '../../../core/models/contractors/contractor-details-dto';
import { MachineOwnerType } from '../../../core/enums/machine-owner-type';
import { MachineStatus } from '../../../core/models/machines/machine-status';

@Injectable({
  providedIn: 'root'
})
export class MachineOwnerService {
  private readonly SVEASKOG_BIOMETRIA_COMPANY_ID = "7778800000";

  constructor(private contractorApiService: ContractorApiService,
    private snackbar: SnackbarService
  ) { }

  public onOwnerTypeChanged(ownerType: MachineOwnerType | null, machineForm: FormGroup<MachineDetailsForm>, isNewMachine: boolean = false): void {
    if (ownerType == null || ownerType !== MachineOwnerType.Internal) {
      machineForm.get('biometriaInfo.biometriaCompanyId')?.markAsDirty();
      machineForm.get('biometriaInfo.biometriaCompanyId')?.setValue("");
      if (!isNewMachine)
        machineForm.get('baseInfo.machineOwnerId')?.setValue(null);

      return;
    }

    machineForm.get('biometriaInfo.biometriaCompanyId')?.markAsDirty();
    machineForm.get('biometriaInfo.biometriaCompanyId')?.setValue(this.SVEASKOG_BIOMETRIA_COMPANY_ID);

    this.snackbar.showResponse(`Sveaskog har Avverkningsföretag-ID ${this.SVEASKOG_BIOMETRIA_COMPANY_ID} och har därför tilldelats maskinen`);
  }

  public async onOwnerChanged(machineOwnerId: number | null, machineForm: FormGroup<MachineDetailsForm>): Promise<void> {
    if (!machineOwnerId) {
      return;
    }

    const contractor = await firstValueFrom(this.contractorApiService.getContractor(machineOwnerId));
    const uniqueBiometriaCompanyId = this.getUniqueBiometriaCompanyIdForContractor(contractor);
    if (!uniqueBiometriaCompanyId) {
      return;
    }

    if (this.getBiometriaCompanyIdValueValidationErrors(uniqueBiometriaCompanyId) !== null) {
      return;
    }

    machineForm.get('biometriaInfo.biometriaCompanyId')?.markAsDirty();
    machineForm.get('biometriaInfo.biometriaCompanyId')?.setValue(uniqueBiometriaCompanyId);

    this.snackbar.showResponse(`${contractor.companyName} har Avverkningsföretag-ID ${uniqueBiometriaCompanyId} och har därför tilldelats maskinen`);
  }

  private getUniqueBiometriaCompanyIdForContractor(contractor: ContractorDetailsDto): string | null {
    const activeContractorMachines = contractor.machines.filter(m => m.status == MachineStatus.Active) || [];

    if (activeContractorMachines.length === 0) {
      return null;
    }

    const biometriaCompanyIds = activeContractorMachines.map(m => m.biometriaCompanyId).filter(id => id !== null && id.length > 0) as string[];
    const uniqueBiometriaCompanyIds = Array.from(new Set(biometriaCompanyIds));
    if (uniqueBiometriaCompanyIds.length !== 1) {
      return null;
    }

    return uniqueBiometriaCompanyIds[0];
  }

  public getBiometriaCompanyIdValueValidationErrors(value: string | null): ValidationErrors | null {
    if (!value) {
      return null;
    }

    const errors: ValidationErrors = {};

    if (value.length !== 10) {
      errors['invalidLength'] = { actualLength: value.length, requiredLength: 10 };
    }

    if (!/^\d+$/.test(value)) {
      errors['notOnlyNumbers'] = { value: value };
    }

    if (!value.endsWith('00000')) {
      errors['notEndingWithZeroes'] = { value: value };
    }

    return Object.keys(errors).length > 0 ? errors : null;
  }
}
